/**
 * Finds sender profiles whose email local part (the text before `@`) collides
 * with another profile's.
 *
 * All profiles share one sender domain, so two identical local parts compose
 * the same `from_email`. The backend stores `from_email` with a unique
 * constraint and validates it per-row, so a request carrying a duplicate fails
 * mid-transaction and rolls the whole channel creation back — surfacing as an
 * opaque 500. Catching it here keeps the user out of that path.
 *
 * Comparison is trimmed and case-insensitive: two sender profiles differing
 * only by surrounding spaces or letter case are a typo, not an intent to
 * create two distinct senders.
 *
 * Blank local parts are ignored — an unfilled profile is handled by the
 * required-field validation instead.
 *
 * @param profiles - Sender profiles in display order.
 * @returns Indexes of every profile taking part in a collision. A collision
 * always yields at least two indexes, so the error can be shown on whichever
 * profile the user is currently viewing.
 */
export const findDuplicateEmailLocals = (
  profiles: ReadonlyArray<{ emailLocal: string }>,
): Set<number> => {
  const indexesByLocal = new Map<string, Array<number>>();

  profiles.forEach((profile, index) => {
    const normalized = profile.emailLocal.trim().toLowerCase();
    if (!normalized) return;
    const existing = indexesByLocal.get(normalized);
    if (existing) {
      existing.push(index);
      return;
    }
    indexesByLocal.set(normalized, [index]);
  });

  const duplicates = new Set<number>();
  indexesByLocal.forEach((indexes) => {
    if (indexes.length < 2) return;
    indexes.forEach((index) => duplicates.add(index));
  });

  return duplicates;
};
